import { useFlags } from 'launchdarkly-react-client-sdk'
import cond from 'lodash/cond'
import entries from 'lodash/entries'
import flatMap from 'lodash/flatMap'
import includes from 'lodash/includes'
import stubTrue from 'lodash/stubTrue'
import {
  incrementalOpen,
  incrementalOpenMachineExcluded,
  openUplift,
  openUpliftMachineExcluded,
} from 'workflow/common/constants'

export type ReportsData = Record<string, string | number | null> | undefined

export type ShowMachineOpensTicked = boolean | undefined

const getFilteredReportsCollection = (
  reports: ReportsData,
  showMachineOpens: boolean,
  showMachineOpensTicked: ShowMachineOpensTicked
) => {
  return Object.entries(reports || {}).filter(([key]) => {
    const flattenedReports = flatMap(entries(reports), (item) => item)

    return cond([
      [
        () => showMachineOpens,
        () => {
          if (includes(flattenedReports, openUpliftMachineExcluded)) {
            if (key === openUpliftMachineExcluded && showMachineOpensTicked) {
              return false
            }
            if (key === openUplift && !showMachineOpensTicked) {
              return false
            }
          }
          if (includes(flattenedReports, incrementalOpenMachineExcluded)) {
            if (
              key === incrementalOpenMachineExcluded &&
              showMachineOpensTicked
            ) {
              return false
            }
            if (key === incrementalOpen && !showMachineOpensTicked) {
              return false
            }
          }
          return true
        },
      ],
      [
        () => !showMachineOpens,
        () =>
          !(
            key === openUpliftMachineExcluded ||
            key === incrementalOpenMachineExcluded
          ),
      ],
      [stubTrue, () => true],
    ])()
  })
}

const getReports = ({
  reports,
  hasReportsData,
  showMachineOpens,
  showMachineOpensTicked,
}: {
  reports: ReportsData
  hasReportsData: boolean
  showMachineOpens: boolean
  showMachineOpensTicked: ShowMachineOpensTicked
}) => {
  // Temporary hide click uplift for in-body projects until the BE is fixed. HH-1774
  if (
    reports &&
    hasReportsData &&
    'In-Body Click Uplift' in reports &&
    'Click Uplift' in reports
  ) {
    delete reports['Click Uplift']
  }

  const filteredReports = hasReportsData
    ? getFilteredReportsCollection(
        reports,
        showMachineOpens,
        showMachineOpensTicked
      )
    : []

  return {
    reportsCellCount: filteredReports.length,
    reportsCollection: filteredReports,
  }
}

const useGetReports = ({ reports, showMachineOpensTicked }) => {
  const { showMachineOpens } = useFlags()

  const hasReportsData = !!(reports && Object.keys(reports).length)

  const { reportsCellCount, reportsCollection } = getReports({
    reports,
    hasReportsData,
    showMachineOpens,
    showMachineOpensTicked,
  })

  return {
    hasReportsData,
    reportsCollection,
    reportsCellCount,
  }
}

export default useGetReports
